import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { timeAgo } from '../utils/timeAgo';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface CustomerChatHistoryProps {
  userId: string;
}

/**
 * Collapsible dropdown of a customer's support AI-chat conversations. Both
 * levels load lazily via `enabled` — conversations on first open, each
 * conversation's turns on expand — so the sidebar stays cheap for tickets from
 * chatty users, while React Query caches what was already fetched.
 */
export function CustomerChatHistory({ userId }: CustomerChatHistoryProps) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const conversationsQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminUserChat(userId),
    queryFn: async () => (await api.getSupportUserChatConversations(userId)).conversations,
    enabled: open,
  });

  const messagesQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminChatMessages(expandedId ?? 'none'),
    queryFn: async () => (await api.getSupportChatMessages(expandedId!)).messages,
    enabled: expandedId !== null,
  });

  const conversations = conversationsQuery.data;
  const count = conversations?.length ?? null;

  return (
    <div className="space-y-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" />
          Chat history{count !== null ? ` (${count})` : ''}
        </h4>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div className="max-h-[320px] overflow-y-auto space-y-1.5 pt-1">
          {conversationsQuery.isLoading ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : conversationsQuery.isError ? (
            <p className="text-xs text-destructive">Couldn’t load chat history.</p>
          ) : conversations && conversations.length > 0 ? (
            conversations.map((c) => (
              <div key={c.conversationId} className="rounded-md border">
                <button
                  onClick={() =>
                    setExpandedId((prev) => (prev === c.conversationId ? null : c.conversationId))
                  }
                  className="flex w-full items-start gap-2 p-2 text-left text-xs hover:bg-muted/50"
                  aria-expanded={expandedId === c.conversationId}
                >
                  <ChevronRight
                    className={`h-3 w-3 mt-0.5 flex-shrink-0 text-muted-foreground transition-transform ${
                      expandedId === c.conversationId ? 'rotate-90' : ''
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-1 font-medium">
                      {c.firstMessage || 'Support chat'}
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      {c.messageCount} message{c.messageCount === 1 ? '' : 's'} ·{' '}
                      {timeAgo(c.updatedAt)}
                    </div>
                  </div>
                </button>

                {expandedId === c.conversationId && (
                  <div className="space-y-2 border-t p-2">
                    {messagesQuery.isLoading ? (
                      <Skeleton className="h-16 w-full" />
                    ) : messagesQuery.isError ? (
                      <p className="text-xs text-destructive">Couldn’t load this conversation.</p>
                    ) : (messagesQuery.data?.length ?? 0) === 0 ? (
                      <p className="text-xs text-muted-foreground">No messages recorded.</p>
                    ) : (
                      messagesQuery.data!.map((m) => (
                        <div key={m.messageId} className="text-xs">
                          <div className="font-medium text-muted-foreground">
                            {m.role === 'user' ? 'Customer' : 'Fias AI'}
                          </div>
                          <div className="whitespace-pre-wrap break-words">{m.content}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground">No AI chat history from this customer.</p>
          )}
        </div>
      )}
    </div>
  );
}
